import { useEffect, useState } from 'react';
import axios from 'axios';
import { Api, ApiPartnerPermissions, Login, PartnerRole, PartnerType, SSOType } from 'vopay';
import vopayApi from '@/services/api.config';

type HookProps = {
  api: Api;
};

export type PortalPartnerLoginRequest = {
  username: string;
} & (
  | {
      password: string;
      SSOType?: never;
      SSOToken?: never;
    }
  | {
      password?: never;
      SSOType: SSOType;
      SSOToken: string;
    }
);

export type PortalPartnerLoginResponse = {
  success: boolean;
  errorMessage: string;
  accountId: string;
  name: string;
  domain: string;
  partnerType: PartnerType;
  permissions: ApiPartnerPermissions;
  role: PartnerRole;
  isInsideSales: boolean;
  MFAEnabled: boolean;
  JWT: string;
};

type ReturnTuple = [
  PortalPartnerLoginResponse | undefined,
  (requestParams: PortalPartnerLoginRequest) => void,
  () => void,
];

function usePostPortalPartnerLogin({ api }: HookProps): ReturnTuple {
  const source = axios.CancelToken.source();

  const [params, setParams] = useState<PortalPartnerLoginRequest>();
  const [response, setResponse] = useState<PortalPartnerLoginResponse>();
  const [shouldSendRequest, setSendRequest] = useState(false);

  useEffect(() => {
    const sendRequest = async (requestParams: PortalPartnerLoginRequest) => {
      const {
        Success,
        ErrorMessage,
        AccountID,
        Name,
        PartnerType,
        Permissions,
        Domain,
        Role,
        IsInsideSales,
        MFAEnabled,
        JWT,
      } = await api.postPortalPartnerLogin(buildParams(requestParams), source);

      const res = {
        success: Success,
        errorMessage: ErrorMessage || '',
        accountId: AccountID || '',
        name: Name || '',
        partnerType: PartnerType || '',
        permissions: Permissions || {},
        domain: Domain || '',
        role: Role || 'Admin',
        isInsideSales: IsInsideSales,
        MFAEnabled: MFAEnabled || false,
        JWT: JWT || '',
      };

      setResponse(res);
      setSendRequest(false);

      return () => {};
    };

    if (params && shouldSendRequest) void sendRequest(params);

    return () => {
      source.cancel();
    };
  }, [shouldSendRequest]);

  return [
    response,
    (requestParams: PortalPartnerLoginRequest) => {
      setParams(requestParams);
      setSendRequest(true);
    },
    () => {
      setResponse(undefined);
      setSendRequest(false);
    },
  ];

  function buildParams(args: PortalPartnerLoginRequest): Login {
    if (isSSOLogin(args)) {
      return {
        SSOType: args.SSOType,
        SSOToken: args.SSOToken,
      };
    }

    return {
      Username: args.username,
      Password: args.password,
    };
  }
}

function isSSOLogin(args: PortalPartnerLoginRequest): args is Extract<PortalPartnerLoginRequest, { SSOToken: string }> {
  return 'SSOType' in args;
}

export default vopayApi.withApiHook<HookProps, ReturnTuple>(usePostPortalPartnerLogin);
